/**
 * Determines whether text should be dark or light based on background color brightness
 * Uses relative luminance calculation to determine contrast
 */

/**
 * Converts hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleanHex = hex.replace("#", "");

  if (cleanHex.length === 3) {
    return {
      r: parseInt(cleanHex[0] + cleanHex[0], 16),
      g: parseInt(cleanHex[1] + cleanHex[1], 16),
      b: parseInt(cleanHex[2] + cleanHex[2], 16),
    };
  }

  if (cleanHex.length === 6) {
    return {
      r: parseInt(cleanHex.substr(0, 2), 16),
      g: parseInt(cleanHex.substr(2, 2), 16),
      b: parseInt(cleanHex.substr(4, 2), 16),
    };
  }

  return null;
}

/**
 * Calculates relative luminance of a color
 * Based on WCAG 2.0 guidelines
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  const rLinear =
    rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear =
    gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear =
    bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * WCAG 2.0 contrast ratio between two relative luminances.
 * Formula: (lighter + 0.05) / (darker + 0.05)
 */
function contrastRatio(lum1: number, lum2: number): number {
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

const WHITE_LUMINANCE = 1;
const DARK_TEXT_LUMINANCE = getRelativeLuminance(0x29, 0x33, 0x3c); // #29333C

/**
 * Determines whether to use dark or light text on a given background color
 * using WCAG 2.0 contrast ratios against white and #29333C.
 * @param backgroundColor - Hex color string (e.g., "#ff5e26" or "ff5e26")
 * @returns "white" for light text or "#29333C" (primary-blue) for dark text
 */
export function getTextColorForBackground(backgroundColor: string): string {
  const rgb = hexToRgb(backgroundColor);

  if (!rgb) {
    return "white";
  }

  const bgLum = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
  const whiteContrast = contrastRatio(WHITE_LUMINANCE, bgLum);
  const darkContrast = contrastRatio(bgLum, DARK_TEXT_LUMINANCE);

  return whiteContrast >= darkContrast ? "white" : "#29333C";
}

const NAV_DARK_TEXT_LUMINANCE = getRelativeLuminance(0x78, 0x7b, 0x7e); // #787b7e

/**
 * Determines whether to use dark or light text on a given background color
 * using WCAG 2.0 contrast ratios against white and #787b7e.
 * @param backgroundColor - Hex color string (e.g., "#ff5e26" or "ff5e26")
 * @returns "white" for light text or "#787b7e" (secondary-blue) for dark text
 */
export function getTextColorForNav(backgroundColor: string): string {
  const rgb = hexToRgb(backgroundColor);

  const secondaryBlue = "#787b7e";
  if (!rgb) {
    return secondaryBlue;
  }

  const bgLum = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
  const whiteContrast = contrastRatio(WHITE_LUMINANCE, bgLum);
  const darkContrast = contrastRatio(bgLum, NAV_DARK_TEXT_LUMINANCE);

  return whiteContrast >= darkContrast ? "#ffffff" : secondaryBlue;
}

/**
 * Determines which color to return based on the contrast of a given background color
 * using WCAG 2.0 contrast ratios.
 * @param backgroundColor - Hex color string (e.g., "#ff5e26" or "ff5e26")
 * @param lightColor - Color to return for dark backgrounds (higher contrast with light)
 * @param darkColor - Color to return for light backgrounds (higher contrast with dark)
 * @returns lightColor for dark backgrounds, darkColor for light backgrounds
 */
export function getColorBasedOnContrast(
  backgroundColor: string,
  lightColor: string,
  darkColor: string
): string {
  const rgb = hexToRgb(backgroundColor);

  if (!rgb) {
    return lightColor;
  }

  const bgLum = getRelativeLuminance(rgb.r, rgb.g, rgb.b);

  const lightRgb = hexToRgb(lightColor);
  const darkRgb = hexToRgb(darkColor);
  const lightLum = lightRgb
    ? getRelativeLuminance(lightRgb.r, lightRgb.g, lightRgb.b)
    : WHITE_LUMINANCE;
  const darkLum = darkRgb
    ? getRelativeLuminance(darkRgb.r, darkRgb.g, darkRgb.b)
    : 0;

  return contrastRatio(lightLum, bgLum) >= contrastRatio(bgLum, darkLum)
    ? lightColor
    : darkColor;
}
